import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatMoney, fromMajor, money } from '@/lib/money'
import { cn } from '@/lib/cn'
import { HOLDINGS } from './crypto-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'

const SPREAD = 0.001

export function CryptoBuyScreen() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [symbol, setSymbol] = useState(params.get('asset') ?? 'USDC')
  const [amount, setAmount] = useState('500')

  const asset = HOLDINGS.find((item) => item.symbol === symbol) ?? HOLDINGS[0]!

  const quote = useMemo(() => {
    const parsed = Number.parseFloat(amount.replace(/,/g, ''))
    const spend = fromMajor(Number.isFinite(parsed) && parsed > 0 ? parsed : 0, 'USD')
    const fee = money(Math.round(spend.minorUnits * SPREAD), 'USD')
    const net = (spend.minorUnits - fee.minorUnits) / 100
    return { spend, fee, units: net / asset.priceUsd, total: spend }
  }, [amount, asset])

  return (
    <>
      <PageHeader
        title="Buy crypto"
        subtitle="0.10% spread, shown in the rate. No separate trading fee."
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to="/crypto">
            Back to wallet
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Stack gap={5}>
              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  What do you want to buy?
                </Text>
                <div className={styles.corridors}>
                  {HOLDINGS.map((item) => (
                    <button
                      key={item.symbol}
                      type="button"
                      className={cn(styles.corridor, item.symbol === symbol && styles.corridorActive)}
                      onClick={() => setSymbol(item.symbol)}
                      aria-pressed={item.symbol === symbol}
                    >
                      {item.symbol === 'USDC' ? (
                        <CurrencyBadge currency="USDC" size={28} />
                      ) : (
                        <Card tone="dark" padded={false} className="pb-coin">
                          <Text variant="eyebrow" weight="bold" tone="accent">
                            {item.symbol}
                          </Text>
                        </Card>
                      )}
                      <Stack gap={0} align="start">
                        <Text variant="caption" weight="semibold">
                          {item.name}
                        </Text>
                        <Text variant="eyebrow" tone="secondary" numeric>
                          ${item.priceUsd.toLocaleString('en-US')}
                        </Text>
                      </Stack>
                    </button>
                  ))}
                </div>
              </Stack>

              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  How much?
                </Text>
                <div className={styles.amountField}>
                  <Text variant="eyebrow" tone="secondary">
                    From your US Dollar account · $18,240.50 available
                  </Text>
                  <div className={styles.amountRow}>
                    <CurrencyBadge currency="USD" size={36} />
                    <input
                      className={styles.amountInput}
                      inputMode="decimal"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                      aria-label="Amount to spend"
                    />
                    <Text variant="subtitle" weight="semibold" tone="secondary">
                      USD
                    </Text>
                  </div>
                </div>
              </Stack>
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                You get
              </Text>
              <Pill tone="accent">{asset.network}</Pill>
            </Card.Header>
            <Stack gap={1} className="pb-mt-4">
              <Text as="p" variant="display" weight="bold" tone="accent" numeric>
                {quote.units.toLocaleString('en-US', {
                  minimumFractionDigits: asset.symbol === 'USDC' ? 2 : 6,
                  maximumFractionDigits: asset.symbol === 'USDC' ? 2 : 6,
                })}
              </Text>
              <Text variant="caption" tone="onDarkMuted">
                {asset.symbol} · 1 {asset.symbol} = ${asset.priceUsd.toLocaleString('en-US')}
              </Text>
            </Stack>

            <div className={checkout.rule} />

            {[
              ['You spend', formatMoney(quote.spend)],
              ['Spread · 0.10%', formatMoney(quote.fee)],
              ['Trading fee', 'None'],
              ['Settles', 'In seconds'],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  tone={index === 2 ? 'accent' : 'onDark'}
                  numeric
                >
                  {value}
                </Text>
              </div>
            ))}

            <Card.Footer>
              <Button
                size="lg"
                block
                iconStart="lock"
                disabled={quote.spend.minorUnits === 0}
                onClick={() => navigate('/crypto/done', { replace: true })}
              >
                Buy {formatMoney(quote.total)} of {asset.symbol}
              </Button>
              <Stack direction="row" align="center" gap={2} className="pb-mt-3">
                <Icon name="info" size={14} />
                <Text variant="eyebrow" tone="onDarkMuted">
                  Crypto is not a deposit and its value can fall to zero.
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
